import React from "react";
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from "@react-pdf/renderer";
import { Spinner } from "reactstrap";

const styles = StyleSheet.create({
  rootTitle: {
    fontSize: 12,
    fontWeight: "bold",
    padding: 8,
    borderBottom: "1pt solid #dee2e6",
    backgroundColor: "#f1f3f5",
    marginBottom: 4,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: "bold",
    padding: 6,
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #dee2e6",
  },
  accountRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 6,
    borderBottom: "1px solid #dee2e6",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 8,
    backgroundColor: "#e9ecef",
    fontWeight: "bold",
    borderTop: "1px solid #dee2e6",
    borderBottom: "1px solid #dee2e6",
    marginTop: 4,
  },
  columnHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 6,
    borderBottom: "1pt solid #adb5bd",
    marginBottom: 4,
  },
  columnHeaderText: {
    fontWeight: "bold",
  },
  accountName: {
    flexGrow: 1,
    flexShrink: 1,
    paddingRight: 8,
  },
  name: {
    fontSize: 10,
  },
  colTotal: {
    width: 105,
    paddingRight: 6,
    textAlign: "right",
    fontSize: 10,
  },
  colSaldo: {
    width: 95,
    textAlign: "right",
    fontSize: 10,
  },
  indent20: { marginLeft: 20 },
  negative: { color: "#dc3545" },
});

const currencyFormat = (num) => {
  return parseFloat(num || 0).toLocaleString("id-ID");
};

const formatSaldo = (saldo) => {
  const value = saldo || 0;
  if (value < 0) {
    return `(${currencyFormat(Math.abs(value))})`;
  }
  return currencyFormat(value);
};

// Akun yang punya induk tidak dirender di level ini, karena sudah dirender di bawah induknya.
const visibleChildren = (node) => {
  return (node.childrens || []).filter((child) => !(child.isAccount && child.parentAccountId));
};

// Total hanya dihitung dari saldo masing-masing akun (bukan rollup) supaya tidak dobel hitung.
const sumAccountsSaldo = (node) => {
  if (!node) return 0;
  if (node.isAccount) return node.headerSaldo || 0;
  return (node.childrens || []).reduce((sum, child) => sum + sumAccountsSaldo(child), 0);
};

const hasAccountDescendant = (node) => {
  return (node.childrens || []).some((child) => (child.isAccount ? true : hasAccountDescendant(child)));
};

const AccountNode = ({ node, depth }) => (
  <View wrap>
    <View style={[styles.accountRow, { marginLeft: 20 + depth * 14 }]}>
      <View style={styles.accountName}>
        <Text style={styles.name}>
          {node.code} - {node.name}
        </Text>
      </View>
      <Text style={styles.colTotal}>{node.hasChildren ? formatSaldo(node.rollupSaldo) : ""}</Text>
      <Text style={styles.colSaldo}>{formatSaldo(node.headerSaldo)}</Text>
    </View>
    {(node.childAccounts || []).map((child) => (
      <AccountNode key={child.id} node={child} depth={depth + 1} />
    ))}
  </View>
);

const GroupNode = ({ node, depth }) => (
  <View wrap>
    <Text style={[styles.groupTitle, { marginLeft: 20 + depth * 14 }]}>{node.name}</Text>
    {visibleChildren(node).map((child) =>
      child.isAccount ? <AccountNode key={child.id} node={child} depth={depth + 1} /> : <GroupNode key={child.id} node={child} depth={depth + 1} />
    )}
    {depth === 0 && hasAccountDescendant(node) && (
      <View style={[styles.totalRow, { marginLeft: 20 + depth * 14 }]}>
        <Text>Total {node.name}</Text>
        <Text>{formatSaldo(sumAccountsSaldo(node))}</Text>
      </View>
    )}
  </View>
);

const BalanceSheetPDF = ({ data }) => {
  return (
    <Document>
      <Page size="A4" style={{ padding: 30, paddingTop: 128, paddingBottom: 112, fontSize: 10, fontFamily: "Helvetica" }} wrap>
        <View fixed style={{ position: "absolute", top: 30, left: 30, right: 30, fontWeight: "bold" }}>
          <View style={{ marginBottom: 20 }}>
            <Text>PT HIKMAH ALAM SENTOSA</Text>
            <Text>NERACA</Text>
            <Text>per tanggal 31/12/2024</Text>
            <Text style={{ fontSize: 8 }}>(dalam IDR)</Text>
          </View>
          <Text style={{ fontSize: 12, fontWeight: "normal", padding: 8, textAlign: "right", backgroundColor: "#00c7ff", color: "#ffffff", marginBottom: 4 }}>{"31/12/2024"}</Text>
        </View>

        <View>
          <View style={styles.columnHeader}>
            <View style={styles.accountName} />
            <Text style={[styles.colTotal, styles.columnHeaderText]}>Total</Text>
            <Text style={[styles.colSaldo, styles.columnHeaderText]}>Saldo</Text>
          </View>

          {data.map((root, rootIndex) => (
            <View key={rootIndex} wrap>
              <Text style={styles.rootTitle}>{root.name}</Text>

              {visibleChildren(root).map((child) =>
                child.isAccount ? (
                  <AccountNode key={child.id} node={child} depth={0} />
                ) : (
                  <GroupNode key={child.id} node={child} depth={0} />
                )
              )}

              <View style={styles.totalRow}>
                <Text>Total {root.name}</Text>
                <Text>{formatSaldo(sumAccountsSaldo(root))}</Text>
              </View>

              <View style={{ height: 20 }} />
            </View>
          ))}
        </View>
        <View fixed style={{ position: "absolute", bottom: 30, left: 30, right: 30 }}>
          <View>
            <Text>*Perhitungan total hanya berlaku pada kepala akun</Text>
            <Text>*Akun persediaan barang dihitung berdasarkan metode Persediaan Rata-Rata</Text>
            <View style={{ height: 1, width: "100%", backgroundColor: "#000000", marginTop: 10, marginBottom: 10 }}></View>
            <View style={{ flexDirection: "row", justifyContent: "space-between", width: "100%" }}>
              <Text>Neraca : PT Hikmah Alam Sentosa per tanggal 31/12/2024</Text>
              <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const Export = ({ data }) => {
  return (
    <PDFDownloadLink document={<BalanceSheetPDF data={data} />} fileName="Laporan_Laba_Rugi.pdf" className="btn btn-success">
      {({ blob, url, loading, error }) => {
        return (
          <>
            <i className="ri-file-download-line me-1 align-bottom fs-14"></i>
            Export {loading ? <Spinner size="sm" className="flex-shrink-0 ms-2" role="status"></Spinner> : ""}
          </>
        );
      }}
    </PDFDownloadLink>
  );
};

export default Export;
